/**
 * QuranWord Component
 * Renders a single Quran word using QCF V2/V4 glyph fonts.
 * Falls back to UthmanicHafs Unicode text while fonts load.
 */

import { cn } from '@/lib/utils';
import type { QuranWord as QuranWordType } from '@/types/quran';

interface QuranWordProps {
  word: QuranWordType;
  isFontLoaded: boolean;
  fontVersion?: 'v2' | 'v4';
  theme?: 'light' | 'dark' | 'sepia';
  onClick?: (word: QuranWordType) => void;
  className?: string;
}

export default function QuranWord({
  word,
  isFontLoaded,
  fontVersion = 'v2',
  theme = 'light',
  onClick,
  className,
}: QuranWordProps) {
  const fontName = `p${word.pageNumber}-${fontVersion}`;

  // Verse end markers — QCF V2/V4 glyphs already contain the ayah number ornament
  // Just render the glyph with the QCF font (same as any other word)
  // No need for separate UthmanicHafs rendering

  // Font loaded → render QCF glyph via innerHTML
  if (isFontLoaded && word.codeV2) {
    const style: React.CSSProperties = {
      fontFamily: fontName,
    };

    // Tajweed V4 font-palette for theming
    if (fontVersion === 'v4') {
      (style as any).fontPalette = theme === 'dark'
        ? `--Dark-${fontName}`
        : theme === 'sepia'
          ? `--Sepia-${fontName}`
          : 'normal';
    }

    return (
      <span
        className={cn(
          'qcf-word',
          word.charTypeName === 'end' && 'verse-end-marker',
          className
        )}
        style={style}
        dangerouslySetInnerHTML={{ __html: word.codeV2 }}
        onClick={() => onClick?.(word)}
      />
    );
  }

  // Fallback: Unicode text while font loads
  return (
    <span
      className={cn('qcf-word qcf-word-loading', className)}
      style={{ fontFamily: "'UthmanicHafs', 'KFGQPC Uthmanic', serif" }}
      onClick={() => onClick?.(word)}
    >
      {word.textQpcHafs || ''}
    </span>
  );
}
